import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Box, Button, TextField } from '@material-ui/core';
import { MinusIcon, PlusIcon } from 'pages/quotes/icons';
import { TextColor } from '@setvi/shared/enums';
import { STooltip } from '@setvi/shared/components';
import clsx from 'clsx';
import { useStyles } from './styles';

const DEBOUNCE_DELAY = 300;

interface QuantitySelectorProps {
  quantity: number;
  min?: number;
  max?: number;
  step?: number;
  required?: boolean;
  onInputChange: (value: number) => void;
  onEnterPress?: () => void;
  onBlur?: () => void;
  inputClassName?: string;
  textFieldClassName?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  oldStyle?: boolean;
}

export const defaultQuantityLimit = 9999999999;

export const QuantitySelector = forwardRef<
  HTMLInputElement,
  QuantitySelectorProps
>(
  (
    {
      quantity,
      min = 0,
      step = 1,
      max = defaultQuantityLimit,
      required,
      onInputChange,
      onEnterPress,
      onBlur,
      inputClassName,
      textFieldClassName,
      disabled = false,
      disabledTooltip = '',
      oldStyle
    },
    ref
  ) => {
    const [error, setError] = useState('');
    const [localQuantity, setLocalQuantity] = useState(quantity);
    const classes = useStyles();
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Debounced submit - only calls API after user stops clicking
    const debouncedSubmit = useCallback(
      (value: number) => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          onInputChange(value);
          onBlur?.();
          debounceTimerRef.current = null;
        }, DEBOUNCE_DELAY);
      },
      [onInputChange, onBlur]
    );

    // Cleanup on unmount
    useEffect(
      () => () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      },
      []
    );

    // Sync from prop when no pending debounce
    useEffect(() => {
      if (!debounceTimerRef.current) {
        setLocalQuantity(quantity);
      }
    }, [quantity]);

    // the second condition is to avoid the case when the quantity is not a number
    const minusDisabled =
      disabled || !Number.isFinite(localQuantity) || localQuantity - step < min;

    const plusDisabled = disabled || localQuantity + step > max;

    const getError = (qty: number) => {
      if (qty === 0) return 'Please enter a quantity value';
      if (qty < min || qty < step) return `Minimum quantity is ${min}`;
      if (qty % step !== 0) return `Quantity must be in multiples of ${step}`;
      if (qty > max) return `Maximum quantity is ${max}`;

      return '';
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle Enter key press
      if (event.key === 'Enter' && onEnterPress) {
        event.preventDefault();
        onEnterPress();
        return;
      }

      // Allow backspace, delete, tab, escape, enter, arrow keys, home, end
      if (
        [
          'Backspace',
          'Tab',
          'Enter',
          'Escape',
          'Home',
          'End',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Delete'
        ].includes(event.key) ||
        // Allow Ctrl+A, Command+A
        (event.key === 'a' && (event.ctrlKey || event.metaKey))
      ) {
        return; // Let it happen
      }

      // Prevent non-digit characters
      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }
    };

    const handlePlusClick = () => {
      const qty = !Number.isFinite(localQuantity) ? 0 : localQuantity;
      if (inputRef?.current) {
        const nextQuantity = qty + step;
        const errorMsg = getError(nextQuantity);

        // Update UI immediately
        inputRef.current.value = nextQuantity.toString();
        setLocalQuantity(nextQuantity);
        setError(errorMsg);

        // Debounce the API call
        if (!errorMsg.length) {
          debouncedSubmit(nextQuantity);
        }
      }
    };

    const handleMinusClick = () => {
      if (inputRef?.current) {
        const qty = !Number.isFinite(localQuantity) ? 0 : localQuantity;
        const nextQuantity = qty - step;

        if (nextQuantity < 1) {
          inputRef.current.value = '';
          setLocalQuantity(0);
          debouncedSubmit(0);
          return;
        }

        const errorMsg = getError(nextQuantity);

        // Update UI immediately
        inputRef.current.value = nextQuantity.toString();
        setLocalQuantity(nextQuantity);
        setError(errorMsg);

        // Debounce the API call
        if (!errorMsg.length) {
          debouncedSubmit(nextQuantity);
        }
      }
    };

    const handleBlur = () => {
      if (!localQuantity) {
        onBlur?.();
        return;
      }
      const isInvalid = getError(Number(inputRef?.current?.value));

      if (isInvalid) {
        setError(getError(localQuantity));
        inputRef.current.value = localQuantity.toString();
      }
      onBlur?.();
    };

    useEffect(() => {
      if (inputRef?.current) {
        inputRef.current.value = localQuantity ? localQuantity.toString() : '';
      }
    }, [inputRef, localQuantity]);

    const tooltipTitle = disabled && disabledTooltip ? disabledTooltip : error;

    return (
      <STooltip title={tooltipTitle} arrow placement="top">
        <Box className={classes.container} onClick={e => e.stopPropagation()}>
          <Button
            className={classes.button}
            variant="text"
            onClick={handleMinusClick}
            disabled={minusDisabled}>
            <MinusIcon
              fill={minusDisabled ? 'rgba(0, 0, 0, 0.26)' : TextColor.DarkGrey}
            />
          </Button>
          <TextField
            error={!!error.length}
            inputRef={inputRef}
            className={clsx(
              oldStyle ? classes?.oldTextField : classes?.textField,
              textFieldClassName
            )}
            defaultValue={quantity}
            disabled={disabled}
            onBlur={handleBlur}
            onPaste={e => {
              const pastedValue = e.clipboardData.getData('text');
              if (!Number.isFinite(pastedValue)) {
                e.preventDefault();
              }
            }}
            onChange={e => {
              if (disabled) return;
              const val =
                e.target.value === '' ? 0 : parseInt(e.target.value, 10);
              inputRef.current.value = e.target.value;

              if (!required) return onInputChange(val);

              setError(getError(val));
              if (!getError(val).length) onInputChange(val);
            }}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              type: 'text',
              pattern: '\\d*',
              onKeyDown: handleKeyDown,
              min,
              max,
              style: {
                minHeight: 39
              },
              className: inputClassName
            }}
          />
          <Button
            className={classes.button}
            variant="text"
            onClick={handlePlusClick}
            disabled={plusDisabled}>
            <PlusIcon
              fill={plusDisabled ? 'rgba(0, 0, 0, 0.26)' : TextColor.DarkGrey}
            />
          </Button>
        </Box>
      </STooltip>
    );
  }
);
